import { useEffect, useRef, useState } from 'react'

import SignatureCanvas from 'react-signature-canvas'
import styled, { css } from 'styled-components'

import { Button } from './BUI/Button'
import { Text } from './BUI/Text'

import { useLocale } from '@/context/localeContext'

type SignatureProps = {
  $hideSignHere: boolean
}

const SignatureWrapper = styled.div<SignatureProps>`
  position: relative;

  .signatureCanvas {
    width: 100%;
    min-height: 160px;
    border: 1px solid ${(props) => props.theme.colour.secondary._300};
    border-radius: ${({ theme }) => theme.borderRadius.small};
    margin-bottom: 0.5rem;
  }

  &::before {
    position: absolute;
    top: 0.75rem;
    left: 0.75rem;
    width: 100%;
    ${(props) => props.theme.font.label02};
    font-weight: 500;
    color: ${(props) => props.theme.colour.secondary._900};
    user-select: none;
    pointer-events: none;
  }

  ${({ $hideSignHere }) =>
    !$hideSignHere &&
    css`
      &::after {
        content: 'Sign here';
        position: absolute;
        top: 50%;
        left: 0;
        transform: translateY(-50%);
        width: 100%;
        text-align: center;
        ${(props) => props.theme.font.body01}
        color: ${(props) => props.theme.colour.secondary._400};
        user-select: none;
        pointer-events: none;
      }
    `}
`

const SignatureClearButton = styled(Button)`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
`

export type SigCanvas = HTMLCanvasElement & {
  clear: () => void
  isEmpty: () => boolean
  toData: () => []
  fromData: (pointGroupArray: []) => void
}

type Props = {
  canvasRef: React.RefObject<SigCanvas>
  onCanvasChange?: () => void
  signatureData?: []
  saveSignature?: (signatureData: []) => void
  labelOverride?: string
}

export const SignatureBox: React.FC<Props> = ({
  canvasRef,
  onCanvasChange,
  signatureData,
  saveSignature,
  labelOverride,
}) => {
  const { t } = useLocale()
  const importAttempted = useRef(false)
  const [canvasDirty, toggleCanvasDirty] = useState(false)
  const [hideSignHere, toggleSignHere] = useState(false)

  useEffect(() => {
    if (!importAttempted.current && canvasRef.current && signatureData) {
      importAttempted.current = true
      canvasRef.current.fromData(signatureData)
      toggleCanvasDirty(true)
      toggleSignHere(true)
      onCanvasChange?.()
    }
  }, [canvasRef, signatureData, onCanvasChange])

  const clearCanvas = () => {
    toggleSignHere(false)
    toggleCanvasDirty(false)

    canvasRef.current.clear()
    saveSignature?.(null)
    onCanvasChange?.()
  }

  const onStart = () => {
    toggleSignHere(true)
    onCanvasChange?.()
  }

  const onEnd = () => {
    toggleCanvasDirty(true)
    onCanvasChange?.()
    saveSignature?.(canvasRef.current.toData())
  }

  return (
    <>
      <Text variant="label02">
        {labelOverride || t('common:labels.yourSignature')}
      </Text>
      <SignatureWrapper $hideSignHere={hideSignHere}>
        <SignatureCanvas
          ref={canvasRef}
          onEnd={onEnd}
          onBegin={onStart}
          canvasProps={{ className: 'signatureCanvas' }}
        />

        <SignatureClearButton
          onClick={clearCanvas}
          variant="secondary-text"
          size={'small'}
          disabled={!canvasDirty}
        >
          {t('common:buttons.clear')}
        </SignatureClearButton>
      </SignatureWrapper>
    </>
  )
}
